import React, { FC, useCallback, useMemo, useRef } from 'react'
import { graphql, useFragment } from 'react-relay'

import { Location, useLocation, useNavigate } from 'react-router-dom'

import { useAuthContext } from '@data/useAuthContext'
import { useMutation } from '@data/useMutation'
import { useAnalyticsContext } from '@data/Analytics'

import {
  Button,
  Form,
  FormFieldContext,
  FormPicker,
  FormPickerItem,
  Text,
} from '@components'

import { AttendEventButton_event$key } from './__generated__/AttendEventButton_event.graphql'
import {
  AttendEventButtonAttendMutation,
  EventAttendeeStatus,
} from './__generated__/AttendEventButtonAttendMutation.graphql'
import { AttendEventButtonRemoveAttendanceMutation } from './__generated__/AttendEventButtonRemoveAttendanceMutation.graphql'

type AttendEventButtonProps = {
  event: AttendEventButton_event$key
  attendLabel?: string
  dontAttendLabel?: string
  primaryButton?: boolean
}

export const AttendEventButton: FC<AttendEventButtonProps> = ({
  event,
  attendLabel = 'Attend',
  dontAttendLabel = "Don't Attend",
  primaryButton = false,
}) => {
  const data = useFragment(
    graphql`
      fragment AttendEventButton_event on Event {
        rowId
        prettyUrl
        hasExternalUrl
        hasVenue
        myAttendance {
          status
          userId
        }

        endAt

        owner {
          __typename
          ... on Guild {
            myMembership {
              id
            }
          }
          ... on User {
            id
          }
        }
      }
    `,
    event
  )

  const attendEvent = useMutation<AttendEventButtonAttendMutation>(
    graphql`
      mutation AttendEventButtonAttendMutation($input: AttendEventInput!) {
        attendEvent(input: $input) {
          event {
            ...EventCard_event
            ...EventEditAttendeesList_event
            ...AttendEventButton_event
            ...EventItem_event
            ...EventScreenNewMobile_event
            ...EventScreenNewDesktop_event
          }
        }
      }
    `
  )

  const removeEventAttendance =
    useMutation<AttendEventButtonRemoveAttendanceMutation>(graphql`
      mutation AttendEventButtonRemoveAttendanceMutation(
        $input: RemoveEventAttendanceInput!
      ) {
        removeEventAttendance(input: $input) {
          event {
            ...EventCard_event
            ...EventEditAttendeesList_event
            ...AttendEventButton_event
            ...EventItem_event
            ...EventScreenNewMobile_event
            ...EventScreenNewDesktop_event
          }
        }
      }
    `)

  const navigate = useNavigate()
  const location = useLocation()
  const { accessToken } = useAuthContext()
  const analytics = useAnalyticsContext()
  const formElementRef = useRef()

  const handleRemoveAttendancePress = useCallback(
    () =>
      removeEventAttendance({
        variables: { input: { eventId: data.rowId } },
      }),
    [data, removeEventAttendance]
  )

  const createAttendEventHandler = useCallback(
    (status: EventAttendeeStatus) => async () => {
      if (!data.hasVenue && !data.hasExternalUrl) {
        throw new Error(
          'Cannot join event without In-Person or Online information'
        )
      }

      await analytics.trackEvent('UBDPSWUB')

      const attendEventUrl = `/events/${data.prettyUrl}/attend`
      const searchParam = `?status=${status}`
      const backgroundLocation =
        ((location.state as { backgroundLocation?: Location })
          ?.backgroundLocation as Location) || location
      const needsConfirmation =
        data.owner?.__typename === 'Guild'
          ? data.owner.myMembership === null
          : true

      if (!accessToken) {
        navigate('/sign-up', {
          state: {
            backgroundLocation,
            authRedirectPath: attendEventUrl + searchParam,
          },
        })
        return
      }

      if (needsConfirmation) {
        navigate({ pathname: attendEventUrl, search: searchParam })
        return
      } else {
        await attendEvent({
          variables: {
            input: { eventId: data.rowId, status },
          },
        })
      }
    },
    [accessToken, analytics, attendEvent, data, location, navigate]
  )

  const eventAttendeeStatusOptions = useMemo(() => {
    switch (data.myAttendance?.status) {
      case 'ATTENDING_IN_PERSON':
        return [
          { label: 'Attending In-Person', value: 'ATTENDING_IN_PERSON' },
          { label: 'Attend Virtually', value: 'ATTENDING_VIRTUAL' },
          { label: dontAttendLabel, value: 'NOT_ATTENDING' },
        ]
      case 'ATTENDING_VIRTUAL':
        return [
          { label: 'Attending Virtually', value: 'ATTENDING_VIRTUAL' },
          { label: 'Attend In-Person', value: 'ATTENDING_IN_PERSON' },
          { label: dontAttendLabel, value: 'NOT_ATTENDING' },
        ]
      case 'NOT_ATTENDING':
        return [
          { label: attendLabel, value: 'NOT_ATTENDING' },
          { label: 'Attend In-Person', value: 'ATTENDING_IN_PERSON' },
          { label: 'Attend Virtually', value: 'ATTENDING_VIRTUAL' },
        ]
      case 'WAITING_IN_PERSON':
        return [
          { label: 'Waiting for In-Person', value: 'WAITING_IN_PERSON' },
          { label: 'Attend Virtually', value: 'ATTENDING_VIRTUAL' },
          { label: dontAttendLabel, value: 'NOT_ATTENDING' },
        ]
      default:
        return [
          { label: attendLabel, value: 'NO_VALUE' },
          { label: 'Attend In-Person', value: 'ATTENDING_IN_PERSON' },
          { label: 'Attend Virtually', value: 'ATTENDING_VIRTUAL' },
        ]
    }
  }, [attendLabel, data, dontAttendLabel])

  const initialValues = useMemo(
    () => ({ status: data.myAttendance?.status ?? 'NO_VALUE' }),
    [data.myAttendance?.status]
  )

  const noop = useCallback(() => {
    // noop
  }, [])

  const handleFormPickerValueChange = useCallback(
    async (status: EventAttendeeStatus) => {
      switch (status) {
        case 'ATTENDING_IN_PERSON':
        case 'ATTENDING_VIRTUAL':
          return await createAttendEventHandler(status)()
        case 'NOT_ATTENDING':
          return handleRemoveAttendancePress()
      }
    },
    [createAttendEventHandler, handleRemoveAttendancePress]
  )

  const formFieldContext = useMemo(
    () => ({ name: 'status', formElementRef }),
    []
  )

  const isHybridEvent = !!(data.hasVenue && data.hasExternalUrl)

  const canAttend = data.hasVenue || data.hasExternalUrl

  const isAttendingStatus = [
    'ATTENDING_IN_PERSON',
    'ATTENDING_VIRTUAL',
    'WAITING_IN_PERSON',
  ].includes(data.myAttendance?.status || '')

  const displayPrimaryButtonStyle = primaryButton && canAttend

  if (!isHybridEvent) {
    return isAttendingStatus ? (
      <Button
        onPress={handleRemoveAttendancePress}
        variant={
          displayPrimaryButtonStyle && !isAttendingStatus
            ? undefined
            : 'secondary'
        }
        hoverVariant={
          displayPrimaryButtonStyle && !isAttendingStatus
            ? 'hover'
            : 'secondaryHover'
        }
        loadingSpinnerSize={16}
        successVariant='success'
        errorVariant='error'
      >
        <Text
          variant={
            displayPrimaryButtonStyle && !isAttendingStatus
              ? 'button'
              : 'buttonSecondary'
          }
          size={4}
        >
          {dontAttendLabel}
        </Text>
      </Button>
    ) : (
      <Button
        variant={displayPrimaryButtonStyle ? undefined : 'secondary'}
        hoverVariant={displayPrimaryButtonStyle ? 'hover' : 'secondaryHover'}
        onPress={createAttendEventHandler(
          data.hasExternalUrl ? 'ATTENDING_VIRTUAL' : 'ATTENDING_IN_PERSON'
        )}
        loadingSpinnerSize={16}
        successVariant='success'
        errorVariant='error'
      >
        <Text
          variant={displayPrimaryButtonStyle ? 'button' : 'buttonSecondary'}
          size={4}
        >
          {attendLabel}
        </Text>
      </Button>
    )
  }

  return (
    <Form initialValues={initialValues} onSubmit={noop}>
      <FormFieldContext.Provider value={formFieldContext}>
        <Button
          variant={
            displayPrimaryButtonStyle && !isAttendingStatus
              ? 'dropDown'
              : 'dropDownSecondary'
          }
          hoverVariant={
            displayPrimaryButtonStyle && !isAttendingStatus
              ? 'dropDownHover'
              : 'dropDownSecondaryHover'
          }
          activeOpacity={1}
          successVariant='success'
          errorVariant='error'
        >
          <FormPicker
            // @ts-expect-error FormPicker type not setup to pass generic to PickerProps
            onValueChange={handleFormPickerValueChange}
            variant={
              displayPrimaryButtonStyle && !isAttendingStatus
                ? 'buttonDropdown'
                : 'buttonDropdownSecondary'
            }
          >
            {eventAttendeeStatusOptions.map((option) => (
              <FormPickerItem
                key={option.value}
                label={option.label}
                value={option.value}
              />
            ))}
          </FormPicker>
        </Button>
      </FormFieldContext.Provider>
    </Form>
  )
}
